import { LISTING_CALLER_SOURCE } from "@tm/api-utils";
import { IListingClientSearchIntent } from "@tm/insurance/api";
import {
  addDaysToDate,
  convertDateToUnix,
  convertUnixToDate,
  replaceHistory,
} from "@tm/insurance/utils";
import { getIndiaTodayDate, getInitialCountryData } from "@tm/static/utils";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { DateRange } from "react-day-picker";
import { ContextEditProps, ICountry } from "./ContextEdit.types";

type OpenCalendar = "start" | "end" | null;

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const weekDayNameFormatter = (date: Date) =>
  React.createElement("span", null, date.toDateString()[0]);

const monthNameFormatter = (date: Date) =>
  React.createElement(
    "span",
    null,
    React.createElement("b", null, MONTHS[date.getMonth()]),
    "\u00A0",
    React.createElement("span", null, date.getFullYear().toString().slice(-2))
  );

export function useContextEditState(props: ContextEditProps) {
  const { travelDetails, tripOption, planType, isDirectLanding, onRefetch } =
    props;
  const { countries, dates, travellerCount } = travelDetails || {};
  const config = dates?.config;
  const isSingleDate =
    config?.isSingleDate === true || tripOption === "MULTITRIP";

  // ──── Country state ────
  const countryListValues = countries?.countryList?.value ?? [];
  const initialCodes = countries?.countryCodes ?? [];
  const initialCountryData = useMemo(
    () => getInitialCountryData(countryListValues, initialCodes) as ICountry[],
    [countryListValues, initialCodes]
  );

  const [countryData, setCountryData] =
    useState<ICountry[]>(initialCountryData);
  const [popularCountries, setPopularCountries] = useState<ICountry[]>(() =>
    initialCountryData.filter((c) => c.isPopular)
  );
  const [showCountry, setShowCountry] = useState<ICountry[]>([]);
  const [showOtherCountry, setShowOtherCountry] = useState(false);

  useEffect(() => {
    setCountryData(initialCountryData);
    setPopularCountries(initialCountryData.filter((c) => c.isPopular));
  }, [initialCountryData]);

  // ──── Date state (matches CommonLandingDT) ────
  const [range, setRange] = useState<DateRange | undefined>(() => {
    const from = convertUnixToDate(dates?.startDate);
    const to = convertUnixToDate(dates?.endDate);
    return from ? { from, to } : undefined;
  });
  const [tempRange, setTempRange] = useState<DateRange>({
    from: undefined,
    to: undefined,
  });
  const [openCalendar, setOpenCalendar] = useState<OpenCalendar>(null);
  const [dateTab, setDateTab] = useState(0);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [currentMonth, setCurrentMonth] = useState(getIndiaTodayDate());
  const handleMonthChange = useCallback((month: Date) => {
    setCurrentMonth(month);
  }, []);

  useEffect(() => {
    const from = convertUnixToDate(dates?.startDate);
    if (!from) return;

    if (isSingleDate && config?.durationAllowed) {
      const autoEnd = addDaysToDate(from, config.durationAllowed);
      setRange({ from, to: autoEnd });
      if (autoEnd) {
        replaceHistory("endDate", convertDateToUnix(autoEnd).toString());
      }
    } else {
      const to = convertUnixToDate(dates?.endDate);
      setRange({ from, to });
    }
  }, [dates?.startDate, dates?.endDate, isSingleDate, config?.durationAllowed]);

  // ──── Traveller state ────
  const [travellers, setTravellers] = useState(travellerCount?.value ?? 1);
  const [showTravellerDropdown, setShowTravellerDropdown] = useState(false);

  useEffect(() => {
    if (travellerCount?.value != null) {
      setTravellers(travellerCount.value);
    }
  }, [travellerCount?.value]);

  // ──── Country error state ────
  const [isCountryError, setIsCountryError] = useState(false);

  // ──── Refs ────
  const travelNameRef = useRef<HTMLDivElement>(null);
  const startCalendarRef = useRef<HTMLDivElement>(null);
  const endCalendarRef = useRef<HTMLDivElement>(null);
  const travellerDropdownRef = useRef<HTMLDivElement>(null);
  const hasAutoOpenedCalendar = useRef(false);
  const hasEditedCountries = useRef(false);

  // ──── Config values ────
  const insPurchaseStartDateMinLimitInDays =
    config?.insPurchaseStartDateMinLimitInDays ?? 0;
  const insPurchaseStartDateLimitInDays =
    config?.insPurchaseStartDateLimitInDays ?? 365;
  const insPurchaseEndDateLimitInDays =
    config?.insPurchaseEndDateLimitInDays ?? 180;
  const startDateErrorMessage =
    config?.startDateErrorMessage ||
    "Policy Start Date should be within 365 days.";
  const endDateErrorMessage =
    config?.endDateErrorMessage ||
    "Policy end date should be within 180 days from policy start date";

  const maxEndDate = useMemo(() => {
    const today = getIndiaTodayDate();
    const effectiveEndLimit = config?.durationAllowed
      ? Math.max(insPurchaseEndDateLimitInDays, config.durationAllowed)
      : insPurchaseEndDateLimitInDays;
    return addDaysToDate(
      today,
      insPurchaseStartDateLimitInDays + effectiveEndLimit
    );
  }, [
    insPurchaseStartDateLimitInDays,
    insPurchaseEndDateLimitInDays,
    config?.durationAllowed,
  ]);

  // ──── Disabled days (matches CommonLandingDT exactly) ────
  const disabledDays = useCallback(
    (date: Date) => {
      const today = getIndiaTodayDate();
      today.setHours(0, 0, 0, 0);
      const minSelectableDate =
        addDaysToDate(today, insPurchaseStartDateMinLimitInDays) || today;
      minSelectableDate.setHours(0, 0, 0, 0);

      const compareDate = new Date(date);
      compareDate.setHours(0, 0, 0, 0);

      if (compareDate < minSelectableDate) return true;
      if (openCalendar === "end" && range?.from) {
        const fromDate = new Date(range.from);
        fromDate.setHours(0, 0, 0, 0);
        if (compareDate < fromDate) return true;
      }
      return !!(maxEndDate && compareDate > maxEndDate);
    },
    [insPurchaseStartDateMinLimitInDays, openCalendar, range?.from, maxEndDate]
  );

  // ──── handleDayClick (matches CommonLandingDT exactly) ────
  const handleDayClick = useCallback(
    (selectedDate: Date) => {
      if (openCalendar === "start") {
        if (dateTab === 0) {
          const maxStartDate = addDaysToDate(
            new Date(),
            insPurchaseStartDateLimitInDays
          );
          if (maxStartDate && maxStartDate < selectedDate) {
            setShowToast(true);
            setToastMessage(startDateErrorMessage);
          } else {
            setTempRange({ from: selectedDate, to: undefined });
            setDateTab(1);
            setShowToast(false);
          }
        } else if (dateTab === 1) {
          if (tempRange.from) {
            if (selectedDate < tempRange.from) {
              setTempRange({ from: selectedDate, to: undefined });
              setShowToast(false);
              setDateTab(1);
            } else {
              const fromDate = new Date(tempRange.from);
              const endLimit = addDaysToDate(
                fromDate,
                insPurchaseEndDateLimitInDays
              );
              if (endLimit && endLimit < selectedDate) {
                setShowToast(true);
                setToastMessage(endDateErrorMessage);
              } else {
                setRange({ from: tempRange.from, to: selectedDate });
                replaceHistory(
                  "startDate",
                  convertDateToUnix(tempRange.from).toString()
                );
                replaceHistory(
                  "endDate",
                  convertDateToUnix(selectedDate).toString()
                );
                setShowToast(false);
                setDateTab(0);
                setTempRange({ from: undefined, to: undefined });
                setOpenCalendar(null);
              }
            }
          }
        }
      } else if (openCalendar === "end") {
        if (range?.from) {
          if (selectedDate < range.from) {
            setRange({ from: selectedDate, to: undefined });
            setOpenCalendar("start");
            setDateTab(1);
          } else {
            const endLimit = addDaysToDate(
              new Date(range.from),
              insPurchaseEndDateLimitInDays
            );
            if (endLimit && endLimit < selectedDate) {
              setShowToast(true);
              setToastMessage(endDateErrorMessage);
            } else {
              setRange({ from: range.from, to: selectedDate });
              replaceHistory(
                "startDate",
                convertDateToUnix(range.from).toString()
              );
              replaceHistory(
                "endDate",
                convertDateToUnix(selectedDate).toString()
              );
              setShowToast(false);
              setOpenCalendar(null);
            }
          }
        }
      }
    },
    [
      openCalendar,
      dateTab,
      tempRange,
      range,
      insPurchaseStartDateLimitInDays,
      insPurchaseEndDateLimitInDays,
      startDateErrorMessage,
      endDateErrorMessage,
    ]
  );

  // ──── handleSingleDateSelect (matches CommonLandingDT) ────
  const handleSingleDateSelect = useCallback(
    (selectedDate: Date) => {
      if (!selectedDate || isNaN(selectedDate.getTime())) return;

      const maxStartDate = addDaysToDate(
        getIndiaTodayDate(),
        insPurchaseStartDateLimitInDays
      );
      if (maxStartDate && maxStartDate < selectedDate) {
        setShowToast(true);
        setToastMessage(startDateErrorMessage);
        return;
      }

      const durationAllowed = config?.durationAllowed;
      const insPurchaseEndDateMinLimitInDays =
        config?.insPurchaseEndDateMinLimitInDays ?? 1;

      let autoEndDate: Date;
      if (durationAllowed) {
        autoEndDate =
          addDaysToDate(selectedDate, durationAllowed) || selectedDate;
      } else {
        autoEndDate =
          addDaysToDate(selectedDate, insPurchaseEndDateMinLimitInDays) ||
          selectedDate;
      }

      if (!autoEndDate || isNaN(autoEndDate.getTime())) {
        autoEndDate = addDaysToDate(selectedDate, 1) || selectedDate;
      }

      setRange({ from: selectedDate, to: autoEndDate });
      replaceHistory("startDate", convertDateToUnix(selectedDate).toString());
      replaceHistory("endDate", convertDateToUnix(autoEndDate).toString());
      setShowToast(false);
      setOpenCalendar(null);
    },
    [config, insPurchaseStartDateLimitInDays, startDateErrorMessage]
  );

  // ──── Calendar open handlers (matches CommonLandingDT) ────
  const handleStartDateClick = useCallback(() => {
    setTempRange(range ?? { from: undefined, to: undefined });
    setOpenCalendar((prev) => (prev === "start" ? null : "start"));
    setShowTravellerDropdown(false);
    setShowOtherCountry(false);
  }, [range]);

  const handleEndDateClick = useCallback(() => {
    if (isSingleDate) return;
    if (openCalendar === "end") {
      setOpenCalendar(null);
    } else if (!range?.from) {
      setOpenCalendar("start");
    } else {
      setOpenCalendar("end");
    }
    setShowTravellerDropdown(false);
    setShowOtherCountry(false);
  }, [openCalendar, range?.from, isSingleDate]);

  // ──── Click-outside for calendar (per-ref, matches CommonLandingDT) ────
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        openCalendar === "start" &&
        startCalendarRef.current &&
        !startCalendarRef.current.contains(event.target as Node)
      ) {
        setOpenCalendar(null);
      }
      if (
        openCalendar === "end" &&
        endCalendarRef.current &&
        !endCalendarRef.current.contains(event.target as Node)
      ) {
        setOpenCalendar(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openCalendar]);

  // ──── Click-outside for traveller (matches CommonLandingDT) ────
  useEffect(() => {
    if (!showTravellerDropdown) return;

    const timeout = setTimeout(() => {
      const el = document.querySelector('[data-selected="true"]');
      if (el) el.scrollIntoView({ block: "center", behavior: "auto" });
    }, 0);

    function handleClickOutside(event: MouseEvent) {
      if (
        travellerDropdownRef.current &&
        !travellerDropdownRef.current.contains(event.target as Node)
      ) {
        setShowTravellerDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      clearTimeout(timeout);
    };
  }, [showTravellerDropdown]);

  // ──── Country toggle (matches CommonLandingDT) ────
  const handleShowOtherCountry = useCallback(() => {
    if (!showOtherCountry) {
      hasEditedCountries.current = true;
      setIsCountryError(false);
      const popularCountrySelections = new Map(
        popularCountries?.map((c: any) => [c.code, c.isSelected]) || []
      );

      let countryDataArr = countryData?.map((country: any) => {
        const isPopularCountry = popularCountrySelections.has(country.code);
        const isSelected = isPopularCountry
          ? popularCountrySelections.get(country.code)
          : country.isSelected;

        return {
          ...country,
          isSelected: isSelected || false,
          isDisabled: false,
          keywords:
            typeof country?.keywords === "string"
              ? country.keywords
                  .split(",")
                  .map((keyword: string) => keyword.trim())
              : country?.keywords,
        };
      });

      const isExclusiveCountrySelected = countryDataArr.find(
        (item: any) => item.isExclusiveSelection && item.isSelected
      );
      countryDataArr = countryDataArr?.map((country: any) => {
        if (isExclusiveCountrySelected) {
          return country.isExclusiveSelection
            ? { ...country }
            : { ...country, isSelected: false, isDisabled: true };
        } else if (countryDataArr.some((item: any) => item.isSelected)) {
          return country.isExclusiveSelection
            ? { ...country, isDisabled: true }
            : { ...country };
        }
        return { ...country };
      });

      const selectedRegionCodes = countryDataArr
        .filter((data: any) => data.isSelected && data.type === "REGION")
        .map((data: any) => data.code);

      selectedRegionCodes?.map((code: string) => {
        countryDataArr = countryDataArr.map((country: any) => {
          if (country.regionCodes && country.regionCodes.includes(code)) {
            return { ...country, isDisabled: true };
          }
          return { ...country };
        });
      });
      setCountryData(countryDataArr);
    }

    let nextCalendar: OpenCalendar = null;
    let nextTravellerDropdown = false;

    if (showOtherCountry) {
      if (isDirectLanding && !hasAutoOpenedCalendar.current) {
        nextCalendar = "start";
        hasAutoOpenedCalendar.current = true;
      } else if (!range?.from) {
        nextCalendar = "start";
      } else if (travellers === 0) {
        nextTravellerDropdown = true;
      }
    }

    setShowOtherCountry(!showOtherCountry);
    setOpenCalendar(nextCalendar);
    setShowTravellerDropdown(nextTravellerDropdown);
  }, [
    showOtherCountry,
    popularCountries,
    countryData,
    range?.from,
    travellers,
    isDirectLanding,
  ]);

  // ──── Traveller toggle ────
  const handleShowTravellerDropdown = useCallback(() => {
    setShowTravellerDropdown((prev) => !prev);
    setOpenCalendar(null);
    setShowOtherCountry(false);
  }, []);

  const handleTravellerSelect = useCallback((num: number) => {
    setTravellers(num);
    replaceHistory("travellers", num.toString());
    setShowTravellerDropdown(false);
  }, []);

  // ──── Derived data ────
  const selectedCountryCodes = useMemo(
    () =>
      countryData
        .filter(
          (c: any) => c.isSelected && !c.isDisabled && c.type !== "HEADER"
        )
        .map((c) => c.code),
    [countryData]
  );

  const displayCountries = useMemo(() => {
    const selectedNames = countryData
      .filter((c: any) => c.isSelected && !c.isDisabled && c.type !== "HEADER")
      .map((c) => (c.subtitle ? `${c.name} ${c.subtitle}` : c.name))
      .join(", ");

    if (selectedNames) return selectedNames;
    if (hasEditedCountries.current) return "";
    return countries?.displayData || "";
  }, [countryData, countries?.displayData]);

  // ──── Format date for display ────
  const formatDate = useCallback((date?: Date): string => {
    if (!date) return "";
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }, []);

  // ──── SEARCH handler ────
  const handleSearch = useCallback(() => {
    if (!selectedCountryCodes.length) {
      setIsCountryError(true);
      return;
    }
    setIsCountryError(false);
    if (!range?.from || !range?.to) return;

    const searchIntent: IListingClientSearchIntent = {
      country: selectedCountryCodes,
      departureDateTime: convertDateToUnix(range.from),
      arrivalDateTime: convertDateToUnix(range.to),
      numberOfTravellers: travellers,
      tripOption,
      planType,
      callerSource: LISTING_CALLER_SOURCE,
    };

    onRefetch?.(searchIntent);
  }, [
    selectedCountryCodes,
    range,
    travellers,
    tripOption,
    planType,
    onRefetch,
  ]);

  return {
    // Country
    countryData,
    setCountryData,
    popularCountries,
    setPopularCountries,
    showCountry,
    setShowCountry,
    showOtherCountry,
    handleShowOtherCountry,
    displayCountries,
    selectedCountryCodes,
    travelNameRef,
    isCountryError,
    setIsCountryError,

    // Calendar
    range,
    tempRange,
    openCalendar,
    disabledDays,
    handleDayClick,
    handleSingleDateSelect,
    handleStartDateClick,
    handleEndDateClick,
    startCalendarRef,
    endCalendarRef,
    isSingleDate,
    currentMonth,
    handleMonthChange,
    weekDayNameFormatter,
    monthNameFormatter,

    // Travellers
    travellers,
    showTravellerDropdown,
    handleShowTravellerDropdown,
    handleTravellerSelect,
    travellerDropdownRef,

    // Toast
    showToast,
    toastMessage,

    // Utils
    formatDate,
    handleSearch,
  };
}
